/*
 * Copyright (c) Nexign, JSC, 2020-2022.
 */
/**
 * Button for Export Errors of import labels
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Evgeniy Naumov
 * @version 1.22.0
 * @since 1.3.0
 */

import { Fragment } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { Button } from '@components/common';
import DensityButton from '@modules/density/components/DensityButton';
import ExportMessage from '@modules/exports/components/ExportMessage';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import { ICommonTranslation } from '@modules/internationalization/types';
import { ECommonTranslationPages } from '@modules/internationalization/types/common/pages';
import { modalOpen } from '@modules/modal/modalActions';
import { IModalOpenPayload } from '@modules/modal/types/modalActionTypes';
import { IModalRenderProps } from '@modules/modal/types/modalTypes';
import { IRootReducer } from '@reducers/rootReducer';

import { labelsImportStartExportErrors } from '../labelsImportActions';
import { ELabelsImportLabelsValidationStatus, ELabelsImportSteps } from '../labelsImportConstants';
import { ILabelsImportLabelsValidationInfo } from '../labelsImportReducer';
import { makeSelectLabelsImportLabelsValidationInfo } from '../labelsImportSelectors';
import { LabelsImportExportErrorButtonContainerStyled } from './styled/LabelsImportStepFinalStyled';

interface ILabelsImportTitleElementsProps {
    stepName: string;
    title: string;
}

type TLabelsImportTitleElementsProps = ILabelsImportTitleElementsProps & IConnectedProps & IDispatchProps;

const LabelsImportTitleElements = ({
    title,
    stepName,
    translation,
    labelsValidationInfo,
    startExportErrors,
    openModal,
}: TLabelsImportTitleElementsProps) => {

    const handleExportError = () => {
        openModal({
            render: (p: IModalRenderProps) => (
                <ExportMessage
                    handleClose={p.onCloseModal}
                    handleSubmit={() => {
                        startExportErrors();
                        p.onCloseModal();
                    }}
                    translation={translation}
                />
            ),
        });
    };

    return (
        <Fragment>
            {stepName === ELabelsImportSteps.Final &&
                (labelsValidationInfo.status === ELabelsImportLabelsValidationStatus.COMPLETED ||
                    labelsValidationInfo.status === ELabelsImportLabelsValidationStatus.INTERRUPTED) && (
                    <Fragment>
                        {labelsValidationInfo.subscriberCount !== labelsValidationInfo.successRowsCount && (
                            <LabelsImportExportErrorButtonContainerStyled>
                                <Button
                                    className="button__button-content_export"
                                    dataName="labels-import-export-errors"
                                    kind="small"
                                    onClick={handleExportError}
                                >
                                    {title}
                                </Button>
                            </LabelsImportExportErrorButtonContainerStyled>
                        )}
                        <DensityButton page={ECommonTranslationPages.labelsImport} />
                    </Fragment>
                )}
        </Fragment>
    );
};

interface IConnectedProps {
    labelsValidationInfo: ILabelsImportLabelsValidationInfo;
    translation: ICommonTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    labelsValidationInfo: makeSelectLabelsImportLabelsValidationInfo(),
    translation: makeSelectTranslationCommon(),
});

interface IDispatchProps {
    startExportErrors: () => void;
    openModal: (payload: IModalOpenPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    startExportErrors: () => dispatch(labelsImportStartExportErrors()),
    openModal: (payload) => dispatch(modalOpen(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(LabelsImportTitleElements);
